/**
 * Yandex.Metrika and Google Ads (gtag), both ported from the legacy
 * `fdm-front` SPA where they were hardcoded in `src/app/index.template.html`.
 *
 * Every id comes from the environment (`VITE_YANDEX_METRIKA_ID`,
 * `VITE_GTAG_ID`, `VITE_GTAG_*_LABEL`), so a stage without the variables
 * (local development, a personal dev stack) loads no tracker at all and every
 * call below becomes a no-op.
 */

type YandexMetrika = (
    counterId: number,
    action: string,
    ...args: unknown[]
) => void;

type Gtag = (...args: unknown[]) => void;

declare global {
    interface Window {
        ym?: YandexMetrika & { a?: unknown[][]; l?: number };
        gtag?: Gtag;
        dataLayer?: unknown[];
    }
}

const counterId = Number(import.meta.env.VITE_YANDEX_METRIKA_ID ?? '');
const enabled = Number.isFinite(counterId) && counterId > 0;

// Google Ads conversion id (`AW-XXXXXXXXXX`) and the labels of the two
// conversion actions. `send_to` is always `<id>/<label>`.
const gtagId = (import.meta.env.VITE_GTAG_ID ?? '').trim();
const gtagEnabled = gtagId !== '';
const gtagUrl = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;

export const adsLabels = {
    /** Legacy "mastering finished" micro conversion. */
    masteringFinished: (import.meta.env.VITE_GTAG_CONVERSION_LABEL ?? '').trim(),
    /** The paid order, the only conversion with a value. */
    purchase: (import.meta.env.VITE_GTAG_PURCHASE_LABEL ?? '').trim(),
};

// The current snippet loads the counter specific tag, which is also what the
// duplicate check below compares against.
const tagUrl = `https://mc.yandex.ru/metrika/tag.js?id=${counterId}`;

/**
 * Injects the counter once. Safe to call from a `StrictMode` double render.
 */
export function initAnalytics(): void {
    initGoogleAds();
    if (!enabled || typeof window === 'undefined' || window.ym) {
        return;
    }
    // The snippet's own guard: a second copy of the tag would double count.
    if (document.querySelector(`script[src="${tagUrl}"]`)) {
        return;
    }

    // The official snippet, rewritten as readable code: queue calls until the
    // real tag.js has loaded and replaced `window.ym`.
    const queue: YandexMetrika & { a?: unknown[][]; l?: number } = (
        ...args: unknown[]
    ) => {
        queue.a = queue.a ?? [];
        queue.a.push(args);
    };
    queue.l = Date.now();
    window.ym = queue;

    // `ecommerce: 'dataLayer'` makes Metrika read the array, so it has to
    // exist before the tag is initialised.
    window.dataLayer = window.dataLayer ?? [];

    const script = document.createElement('script');
    script.async = true;
    script.src = tagUrl;
    document.head.appendChild(script);

    window.ym(counterId, 'init', {
        ssr: true,
        webvisor: true,
        clickmap: true,
        ecommerce: 'dataLayer',
        referrer: document.referrer,
        url: location.href,
        accurateTrackBounce: true,
        trackLinks: true,
    });

    appendNoScriptPixel();
}

/**
 * Reports a virtual page view. The SPA never reloads, so Metrika would
 * otherwise only ever see the entry URL.
 */
export function trackPageView(url: string, title?: string): void {
    if (!enabled) {
        return;
    }
    window.ym?.(counterId, 'hit', url, title ? { title } : undefined);
}

/**
 * Reports a conversion goal, e.g. `purchase` after a captured payment.
 * Goals have to exist in the Metrika dashboard to show up in reports.
 */
export function trackGoal(goal: string, params?: Record<string, unknown>): void {
    if (!enabled) {
        return;
    }
    window.ym?.(counterId, 'reachGoal', goal, params);
}

/**
 * Injects `gtag.js` for the Google Ads conversion id. Like the Metrika part it
 * is a no-op without the id, so dev stages never touch the Ads statistics.
 */
function initGoogleAds(): void {
    if (!gtagEnabled || typeof window === 'undefined' || window.gtag) {
        return;
    }
    if (document.querySelector(`script[src="${gtagUrl}"]`)) {
        return;
    }

    // The official snippet: `dataLayer` collects the calls until gtag.js has
    // loaded, which is why `arguments` (not an array) has to be pushed.
    window.dataLayer = window.dataLayer ?? [];
    const gtag: Gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments);
    };
    window.gtag = gtag;

    const script = document.createElement('script');
    script.async = true;
    script.src = gtagUrl;
    document.head.appendChild(script);

    gtag('js', new Date());
    gtag('config', gtagId);
}

/**
 * Reports a Google Ads conversion action.
 *
 * `label` is the conversion label of a single action (see `adsLabels`), and
 * `transaction_id` is what lets Google de-duplicate: without it a reload of
 * the success screen counts as a second sale and the bidding data is junk.
 */
export function trackAdsConversion(
    label: string,
    params?: Record<string, unknown>,
): void {
    if (!gtagEnabled || !label) {
        return;
    }
    window.gtag?.('event', 'conversion', {
        send_to: `${gtagId}/${label}`,
        ...params,
    });
}

/**
 * The `<noscript>` pixel of the legacy snippet, kept for parity. It only
 * matters for clients that block the script but load images.
 */
function appendNoScriptPixel(): void {
    const noscript = document.createElement('noscript');
    const image = document.createElement('img');
    image.src = `https://mc.yandex.ru/watch/${counterId}`;
    image.alt = '';
    image.style.position = 'absolute';
    image.style.left = '-9999px';
    noscript.appendChild(image);
    document.body.appendChild(noscript);
}
